/**
 * Consent management core.
 *
 * Single source of truth for what the visitor has allowed. Everything that
 * could set a non-essential cookie, load a third-party script or emit a
 * tracking call must ask this module first.
 *
 * Design constraints that follow from GDPR/ePrivacy + CCPA/CPRA:
 *  - The pre-decision default depends on where the visitor is. Under the
 *    `strict` regime nothing non-essential may run before an affirmative
 *    action; under `default_on` analytics starts granted and the banner offers
 *    an opt-out. Marketing and personalization always require an opt-in, in
 *    every region. See ConsentRegion.ts for which is which, and note that it
 *    fails closed to `strict` whenever the location is unknown.
 *  - The stored record is versioned and timestamped so we can re-prompt on a
 *    material policy change or on expiry, and only then.
 *  - Withdrawal must be as easy as granting, and must actively clean up
 *    cookies/storage the third parties already wrote.
 *  - Global Privacy Control is honoured as an opt-out signal (CPRA), and
 *    outranks a `default_on` default.
 */

import type { ConsentRegime } from './ConsentRegion';

export const CONSENT_COOKIE_NAME = 'th-consent';

/**
 * Bump when the categories change or the privacy notice changes materially.
 * A record written under an older version is treated as undecided, which
 * re-shows the banner. Do NOT bump for copy tweaks.
 */
export const CONSENT_VERSION = 2;

/** ePrivacy/CNIL guidance: a consent record should not outlive ~13 months. */
export const CONSENT_MAX_AGE_DAYS = 365;

export type ConsentCategory =
    | 'necessary'
    | 'analytics'
    | 'marketing'
    | 'personalization';

export type ConsentState = Record<ConsentCategory, boolean>;

export type ConsentMethod = 'accept_all' | 'reject_all' | 'custom';

export type ConsentRecord = {
    /** CONSENT_VERSION at the time of the decision. */
    v: number;
    /** Decision timestamp, epoch ms. */
    ts: number;
    method: ConsentMethod;
    categories: ConsentState;
};

/** Pre-consent state, and the state a "Reject all" produces. */
export const CONSENT_DENIED_ALL: ConsentState = Object.freeze({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false
});

export const CONSENT_GRANTED_ALL: ConsentState = Object.freeze({
    necessary: true,
    analytics: true,
    marketing: true,
    personalization: true
});

/**
 * The pre-decision state under the `default_on` regime.
 *
 * Analytics only. Marketing stays denied because it covers cross-context
 * advertising and session replay, neither of which we default anyone into, and
 * personalization stays denied because it is not a measurement purpose.
 */
export const CONSENT_ANALYTICS_ONLY: ConsentState = Object.freeze({
    necessary: true,
    analytics: true,
    marketing: false,
    personalization: false
});

/** The state a visitor starts in, before any decision, for a given regime. */
export const defaultConsentState = (regime: ConsentRegime): ConsentState => (
    regime === 'default_on' ? { ...CONSENT_ANALYTICS_ONLY } : { ...CONSENT_DENIED_ALL }
);

type GtagConsentValue = 'granted' | 'denied';

type GtagConsentPayload = {
    ad_storage: GtagConsentValue;
    ad_user_data: GtagConsentValue;
    ad_personalization: GtagConsentValue;
    analytics_storage: GtagConsentValue;
    personalization_storage: GtagConsentValue;
    functionality_storage: GtagConsentValue;
    security_storage: GtagConsentValue;
};

declare global {
    interface Window {
        dataLayer?: unknown[];
        // Defined by the inline consent-defaults script in the document head.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gtag?: (...args: any[]) => void;
    }
    interface Navigator {
        /** Global Privacy Control. Non-standard but shipping in several browsers. */
        globalPrivacyControl?: boolean;
    }
}

const isBrowser = () => typeof document !== 'undefined';

const clampState = (partial: Partial<ConsentState> | undefined): ConsentState => ({
    // Strictly necessary storage is not optional and is never presented as such.
    necessary: true,
    analytics: partial?.analytics === true,
    marketing: partial?.marketing === true,
    personalization: partial?.personalization === true
});

/**
 * Parse a raw cookie value into a record.
 *
 * Returns null for anything unreadable, for a different consent version, and
 * for the legacy notice-only value (`th-consent=true`). Legacy visitors are
 * deliberately re-prompted: a single-button "we use cookies" notice was never
 * valid consent for analytics/marketing, so it cannot be carried forward.
 */
export const parseConsentCookie = (raw: string | null | undefined): ConsentRecord | null => {
    if (!raw) return null;

    let decoded = raw;
    try {
        decoded = decodeURIComponent(raw);
    } catch (error) {
        /* value was not encoded; fall through with the raw string */
    }

    if (decoded === 'true' || decoded === 'false') {
        // Legacy notice-only cookie. Not granular consent -> re-prompt.
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(decoded);
    } catch (error) {
        return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;

    const candidate = parsed as Partial<ConsentRecord>;
    if (candidate.v !== CONSENT_VERSION) return null;
    if (typeof candidate.ts !== 'number' || !Number.isFinite(candidate.ts)) return null;

    return {
        v: CONSENT_VERSION,
        ts: candidate.ts,
        method: candidate.method === 'accept_all' || candidate.method === 'reject_all' ? candidate.method : 'custom',
        categories: clampState(candidate.categories)
    };
};

export const isConsentRecordExpired = (record: ConsentRecord, now = Date.now()) =>
    now - record.ts > CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

/** A record only counts as a decision while it is current and unexpired. */
export const isConsentRecordUsable = (record: ConsentRecord | null, now = Date.now()): record is ConsentRecord =>
    !!record && record.v === CONSENT_VERSION && !isConsentRecordExpired(record, now);

/**
 * Cookie domain for the consent record.
 *
 * A leading dot so the decision is shared with every `*.therealest.com`
 * subdomain and the visitor is not re-prompted after navigating between them.
 * Omitted on localhost / preview hosts, where a cross-domain cookie is
 * silently rejected by the browser.
 */
const consentCookieDomain = (): string | null => {
    if (!isBrowser()) return null;
    const { hostname } = window.location;
    if (hostname === 'therealest.com' || hostname.endsWith('.therealest.com')) {
        return '.therealest.com';
    }
    return null;
};

export const writeConsentRecord = (record: ConsentRecord) => {
    if (!isBrowser()) return;
    const value = encodeURIComponent(JSON.stringify(record));
    const parts = [
        `${CONSENT_COOKIE_NAME}=${value}`,
        'Path=/',
        `Max-Age=${CONSENT_MAX_AGE_DAYS * 24 * 60 * 60}`,
        'SameSite=Lax'
    ];
    const domain = consentCookieDomain();
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
};

export const buildConsentRecord = (categories: Partial<ConsentState>, method: ConsentMethod): ConsentRecord => ({
    v: CONSENT_VERSION,
    ts: Date.now(),
    method,
    categories: clampState(categories)
});

/* ------------------------------------------------------------------ *
 * Global Privacy Control
 * ------------------------------------------------------------------ */

/**
 * True when the browser is broadcasting an opt-out preference.
 *
 * Under CPRA this is a valid opt-out of sale/sharing and must be honoured
 * without asking. It is NOT consent under GDPR, so the banner still appears —
 * but every non-essential toggle starts off and nothing fires until the
 * visitor affirmatively opts in.
 */
export const isGlobalPrivacyControlEnabled = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    try {
        return navigator.globalPrivacyControl === true;
    } catch (error) {
        return false;
    }
};

/* ------------------------------------------------------------------ *
 * Google Consent Mode v2
 * ------------------------------------------------------------------ */

export const toGtagConsentPayload = (state: ConsentState): GtagConsentPayload => {
    const grant = (allowed: boolean): GtagConsentValue => (allowed ? 'granted' : 'denied');
    return {
        ad_storage: grant(state.marketing),
        ad_user_data: grant(state.marketing),
        ad_personalization: grant(state.marketing),
        analytics_storage: grant(state.analytics),
        personalization_storage: grant(state.personalization),
        // Strictly necessary buckets. Kept explicit so GTM never has to guess.
        functionality_storage: 'granted',
        security_storage: 'granted'
    };
};

const pushConsentUpdate = (state: ConsentState) => {
    if (typeof window === 'undefined') return;
    const payload = toGtagConsentPayload(state);
    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', payload);
    } else {
        // The defaults script did not run (blocked, or GTM not configured).
        // Queue the command so a later container pickup still sees it.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(['consent', 'update', payload]);
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'th_consent_update', th_consent: state });
};

/* ------------------------------------------------------------------ *
 * Current state + subscriptions
 * ------------------------------------------------------------------ */

let currentState: ConsentState = { ...CONSENT_DENIED_ALL };
let hasDecision = false;

const listeners = new Set<(state: ConsentState) => void>();

const emit = () => {
    listeners.forEach((listener) => {
        try {
            listener(currentState);
        } catch (error) {
            /* a broken subscriber must not break consent propagation */
        }
    });
};

/**
 * Seed the in-memory state. Called once on the client with the record the
 * server read from the request cookie, so the very first render already knows
 * the answer (no banner flash for returning visitors, no hydration mismatch).
 *
 * With no usable record the state falls back to the regime default, which for
 * `default_on` means analytics is live before the visitor has done anything.
 * `hasDecision` stays false in that case — a default is not a decision, so the
 * banner still appears and the record is still written the moment they choose.
 *
 * A client-side GPC signal forces `strict` regardless of the regime the server
 * resolved. The server already checks `Sec-GPC`, so this only matters when that
 * header was stripped in transit, but the cost of the extra check is nothing
 * and the cost of missing a GPC opt-out is a CPRA violation.
 */
export const hydrateConsent = (record: ConsentRecord | null, regime: ConsentRegime = 'strict') => {
    if (isConsentRecordUsable(record)) {
        currentState = { ...record.categories };
        hasDecision = true;
    } else {
        const effective = isGlobalPrivacyControlEnabled() ? 'strict' : regime;
        currentState = defaultConsentState(effective);
        hasDecision = false;
        // Bring Consent Mode in line with a granted default. The inline script
        // already did this server-side; repeating it costs one dataLayer push
        // and covers the case where that script was blocked.
        if (effective === 'default_on') pushConsentUpdate(currentState);
    }
    emit();
};

export const getConsentState = (): ConsentState => currentState;

export const hasConsentDecision = (): boolean => hasDecision;

/** The gate every tracker must pass through. */
export const hasConsent = (category: ConsentCategory): boolean => currentState[category] === true;

export const subscribeToConsent = (listener: (state: ConsentState) => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/* ------------------------------------------------------------------ *
 * Cleanup on withdrawal
 * ------------------------------------------------------------------ */

/**
 * Cookies written by the third parties we load, grouped by the category that
 * authorises them. Matched by prefix because most carry a dynamic suffix
 * (`_ga_<container>`, `_hjSession_<site>`, ...).
 *
 * Keep this list in step with the vendors named in the privacy notice.
 */
const THIRD_PARTY_COOKIE_PREFIXES: Record<Exclude<ConsentCategory, 'necessary'>, string[]> = {
    analytics: [
        '_ga', // Google Analytics client id (+ `_ga_*` per-property)
        '_gid',
        '_gat',
        'ajs_', // Segment anonymous/user id
        'th-session' // first-party visit id, analytics only
    ],
    marketing: [
        '_fbp', // Meta pixel browser id
        '_fbc',
        'fr',
        '_gcl_', // Google Ads click ids
        '_gac_',
        'IDE',
        'test_cookie',
        '_hj' // Hotjar session/recording cookies - session replay, see notice
    ],
    personalization: []
};

const LOCAL_STORAGE_PREFIXES: Record<Exclude<ConsentCategory, 'necessary'>, string[]> = {
    analytics: ['ajs_'],
    marketing: ['_hj'],
    personalization: []
};

const deleteCookieEverywhere = (name: string) => {
    if (!isBrowser()) return;
    const { hostname } = window.location;
    // A cookie must be cleared with the same domain/path it was set with, and
    // we do not know which the vendor used - so try each plausible scope.
    const domains = new Set<string>(['', hostname]);
    const parts = hostname.split('.');
    for (let i = 0; i < parts.length - 1; i += 1) {
        domains.add(`.${parts.slice(i).join('.')}`);
    }
    domains.forEach((domain) => {
        const attrs = ['Path=/', 'Expires=Thu, 01 Jan 1970 00:00:00 GMT'];
        if (domain) attrs.push(`Domain=${domain}`);
        document.cookie = `${name}=; ${attrs.join('; ')}`;
    });
};

/**
 * Remove cookies and storage belonging to categories that are now denied.
 *
 * Without this, a visitor who opts out keeps being identified by the `_ga` /
 * `_fbp` / `ajs_anonymous_id` values already on their device — the opt-out
 * would look honoured in the network tab while the identifiers persisted.
 */
export const clearDeniedStorage = (state: ConsentState) => {
    if (!isBrowser()) return;

    const deniedCategories = (Object.keys(THIRD_PARTY_COOKIE_PREFIXES) as Exclude<ConsentCategory, 'necessary'>[])
        .filter((category) => state[category] !== true);

    if (!deniedCategories.length) return;

    const cookiePrefixes = deniedCategories.flatMap((category) => THIRD_PARTY_COOKIE_PREFIXES[category]);
    const existingNames = document.cookie
        .split(';')
        .map((entry) => entry.split('=')[0]?.trim())
        .filter((name): name is string => !!name);

    existingNames
        .filter((name) => cookiePrefixes.some((prefix) => name.startsWith(prefix)))
        .forEach(deleteCookieEverywhere);

    const storagePrefixes = deniedCategories.flatMap((category) => LOCAL_STORAGE_PREFIXES[category]);
    if (!storagePrefixes.length) return;

    try {
        Object.keys(window.localStorage)
            .filter((key) => storagePrefixes.some((prefix) => key.startsWith(prefix)))
            .forEach((key) => window.localStorage.removeItem(key));
    } catch (error) {
        /* storage can be unavailable (Safari private mode, blocked cookies) */
    }
};

/* ------------------------------------------------------------------ *
 * Applying a decision
 * ------------------------------------------------------------------ */

/**
 * Persist a decision, propagate it to every gated integration and clean up
 * storage for anything the visitor turned off.
 */
/**
 * Ask the server to re-issue the cookie via `Set-Cookie`.
 *
 * Safari's ITP caps cookies written from `document.cookie` at seven days, which
 * would silently expire a stored decision and re-prompt a Safari visitor every
 * week. The local write above already took effect, so this is best-effort: a
 * failure costs cookie lifetime, never correctness.
 */
const syncConsentToServer = (record: ConsentRecord | null) => {
    if (!isBrowser() || typeof fetch !== 'function') return;
    const request = record
        ? fetch('/api/consent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ categories: record.categories, method: record.method }),
            keepalive: true
        })
        : fetch('/api/consent', { method: 'DELETE', keepalive: true });

    request.catch(() => {
        /* offline or blocked - the client-side cookie still stands */
    });
};

export const setConsent = (categories: Partial<ConsentState>, method: ConsentMethod): ConsentState => {
    const record = buildConsentRecord(categories, method);
    currentState = { ...record.categories };
    hasDecision = true;

    writeConsentRecord(record);
    syncConsentToServer(record);
    pushConsentUpdate(currentState);
    clearDeniedStorage(currentState);
    emit();

    return currentState;
};

export const acceptAllConsent = () => setConsent(CONSENT_GRANTED_ALL, 'accept_all');

export const rejectAllConsent = () => setConsent(CONSENT_DENIED_ALL, 'reject_all');

/**
 * Clear the stored decision so the banner reappears. Non-essential storage is
 * purged at the same time, so withdrawal takes effect immediately rather than
 * at the next decision.
 *
 * Under `default_on` an absent record is not a neutral state — it *is* the
 * granted default, so deleting the cookie would hand the visitor straight back
 * to live analytics on their next page load and make the opt-out look like it
 * silently failed. There, withdrawal is stored as an explicit `reject_all`
 * instead. An opt-out has to survive a reload to be an opt-out.
 */
export const withdrawConsent = (regime: ConsentRegime = 'strict') => {
    if (regime === 'default_on') {
        setConsent(CONSENT_DENIED_ALL, 'reject_all');
        return;
    }

    if (isBrowser()) {
        const parts = [
            `${CONSENT_COOKIE_NAME}=`,
            'Path=/',
            'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
            'SameSite=Lax'
        ];
        const domain = consentCookieDomain();
        if (domain) parts.push(`Domain=${domain}`);
        document.cookie = parts.join('; ');
    }
    currentState = { ...CONSENT_DENIED_ALL };
    hasDecision = false;
    syncConsentToServer(null);
    pushConsentUpdate(currentState);
    clearDeniedStorage(currentState);
    emit();
};

/**
 * The inline script injected into <head> before anything else runs.
 *
 * It has to be inline and synchronous: Consent Mode defaults are only
 * effective if they are queued before the GTM container evaluates its tags.
 * `wait_for_update` gives the client a short window to send the stored
 * decision before tags decide to fire in default (denied) mode.
 *
 * The `default` command is all-denied in every region, including `default_on`.
 * A granted default followed by an immediate `update` is equivalent when the
 * script runs to completion, and strictly safer when it does not: whatever
 * interrupts it leaves the visitor denied rather than tracked.
 *
 * @param initialRecord the usable record the server read from the request
 *   cookie, or null when the visitor has not decided yet.
 * @param regime the regime resolved from the request's geo headers. Defaults to
 *   `strict` so a caller that forgets to pass it cannot widen consent.
 */
export const buildConsentDefaultsScript = (
    initialRecord: ConsentRecord | null,
    regime: ConsentRegime = 'strict'
): string => {
    const defaults = toGtagConsentPayload(CONSENT_DENIED_ALL);
    const decided = isConsentRecordUsable(initialRecord);
    const applied = decided
        ? initialRecord.categories
        : (regime === 'default_on' ? CONSENT_ANALYTICS_ONLY : null);
    const update = applied
        ? `gtag('consent','update',${JSON.stringify(toGtagConsentPayload(applied))});`
        : '';

    return `
window.dataLayer=window.dataLayer||[];
function gtag(){window.dataLayer.push(arguments);}
window.gtag=window.gtag||gtag;
gtag('consent','default',${JSON.stringify({ ...defaults, wait_for_update: 500 })});
${update}`.trim();
};
